/**
 * Important - implement isLongTouchPending on host if needed (eg. host already has click event listener attached, see `ChatListItem`)
 *
 * isLongTouchPending - Use this to check on host if longTouch is in progress and you can early return from click event if needed
 *
 * This works differently on devices. Currently tested on Chat list item & chat message:
 *   * App and Safari iPhone 15 Pro, iOS 17
 *   * App and Safari iPad Pro 6th generation, iOS 17.0 (App)
 *   * Desktop browsers without touch (Chrome, Firefox, Safari)
 *   * Desktop Browsers with touch (Chrome, Firefox, Safari)
 */

export class LongTouchController {
  constructor(host, { callback, delay = 500 }) {
    this.host = host;
    this.callback = callback;
    this.delay = delay;
    this.host.isLongTouchPending = false;

    host.addController(this);
  }

  hostConnected() {
    const { host } = this;

    const config = { passive: true };
    host.addEventListener('touchstart', this, config);
    host.addEventListener('touchend', this, config);
    host.addEventListener('touchcancel', this, config);
    host.addEventListener('touchmove', this, config);
  }

  hostDisconnected() {
    const { host } = this;

    host.removeEventListener('touchstart', this);
    host.removeEventListener('touchend', this);
    host.removeEventListener('touchcancel', this);
    host.removeEventListener('touchmove', this);
  }

  handleEvent(e) {
    // console.log('[LONG_TOUCH] handleEvent', e.type);
    const method = {
      touchstart: 'start',
      touchend: 'end',
      touchcancel: 'end',
      touchmove: 'end',
    }[e.type];

    this[method](e);
  }

  start(e) {
    // Skip for links
    if (e.target.tagName === 'A') return;

    // console.log('[LONG_TOUCH] touch start', e.type, e);
    this.setStyles();
    this.setupContextMenuEvent();
    this.timeout = setTimeout(() => {
      this.host.isLongTouchPending = true;
      /* On some devices there is an extra (ghost) click after touch event and we need to prevent it if long touch callback is triggered */
      this.host.addEventListener('click', this.preventAll, { once: true });
      // console.log('[LONG_TOUCH] execute callback', e.type, e);
      this.callback(e);
    }, this.delay);
  }

  end() {
    clearTimeout(this.timeout);
    this.revertStyles();

    if (this.host.isLongTouchPending) {
      /* We prevented ghost click if long touch callback was triggered and now we have to remove that event listener *
      /* It is necessary to add delay as the click comes after the touch end event */
      setTimeout(() => {
        this.host.isLongTouchPending = false;
        this.host.removeEventListener('click', this.preventAll);
        // console.log('[LONG_TOUCH] removeEventListener CLICK');
      }, 500);
    }
    // console.log('[LONG_TOUCH] touch end', e);
  }

  /* On some devices (e.g. iPad Pro 6th generation, iOS 17.0) 'contextmenu' event is not triggered and therefor we have to prevent user selection */
  setStyles() {
    const { userSelect, MozUserSelect, webkitUserSelect } = this.host.style;

    this.defaultStyles = {
      userSelect,
      MozUserSelect,
      webkitUserSelect,
    };

    this.host.style.userSelect = 'none';
    this.host.style.MozUserSelect = 'none';
    this.host.style.webkitUserSelect = 'none';
  }

  revertStyles() {
    Object.assign(this.host.style, this.defaultStyles);
  }

  setupContextMenuEvent() {
    // console.log('[LONG_TOUCH] setup context menu event');
    this.host.addEventListener('contextmenu', this.preventAll, {
      once: true,
    });
  }

  preventAll(e) {
    // console.log('[LONG_TOUCH] preventAll', e.type, e);
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
}
